// components/learning/InlineMathField.jsx
import React, { useState, useRef, useEffect } from 'react';
import { MathfieldElement } from 'mathlive';
import { GripVertical, X } from 'lucide-react';

const InlineMathField = ({ value, onChange, placeholder, rows = 3 }) => {
  const textareaRef = useRef(null);
  const [showMathEditor, setShowMathEditor] = useState(false);
  const [mathContent, setMathContent] = useState('');
  const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });
  const mathEditorRef = useRef(null);
  const modalRef = useRef(null);
  
  // Position state for the draggable editor
  const [editorPosition, setEditorPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  
  // Ensure MathfieldElement is registered as a custom element
  useEffect(() => {
    if (!customElements.get('math-field')) {
      customElements.define('math-field', MathfieldElement);
    }
  }, []);
  
  // Add Ctrl+M shortcut to text area
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        openMathEditor();
      }
    };
    
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('keydown', handleKeyDown);
      return () => {
        textarea.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [value]);

  // Use Escape to insert and close the math editor
  useEffect(() => {
    if (!showMathEditor) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMathEditor(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMathEditor, mathContent, cursorPosition, value]);
  
  const openMathEditor = () => {
    // Get cursor position and selected text
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = value.substring(start, end);
      
      // Store cursor position for later
      setCursorPosition({ start, end });
      
      // Set initial math content (strip $ if present)
      if (selectedText) {
        if (selectedText.startsWith('$') && selectedText.endsWith('$')) {
          setMathContent(selectedText.slice(1, -1));
        } else {
          setMathContent(selectedText);
        }
      } else {
        setMathContent('');
      }
      
      // Calculate initial position near cursor
      const rect = textarea.getBoundingClientRect();
      const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
      const linesBeforeCursor = value.substring(0, start).split('\n').length - 1;
      const approximateY = rect.top + linesBeforeCursor * lineHeight + 50;
      
      setEditorPosition({
        x: Math.max(100, Math.min(window.innerWidth - 400, rect.left + 50)),
        y: Math.max(100, Math.min(window.innerHeight - 300, approximateY))
      });
      
      // Show math editor
      setShowMathEditor(true);
      
      // Focus math editor after it's visible
      setTimeout(() => {
        if (mathEditorRef.current) {
          mathEditorRef.current.focus();
        }
      }, 50);
    }
  };
  
  const closeMathEditor = (insertMath = true) => {
    if (insertMath && mathContent && textareaRef.current) {
      const { start, end } = cursorPosition;
      const newText = 
        value.substring(0, start) + 
        `$${mathContent}$` + 
        value.substring(end);
      
      // Update the main text area
      onChange(newText);
      
      // Calculate new cursor position after the inserted math
      const newPosition = start + mathContent.length + 2; // +2 for the $ symbols
      
      // Set cursor position after state update
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newPosition, newPosition);
        }
      }, 0);
    }
    
    // Reset and hide math editor
    setShowMathEditor(false);
    setMathContent('');
  };

  // MOUSE DRAG HANDLERS
  const startDrag = (e) => {
    setIsDragging(true);
    setDragStartPos({
      x: e.clientX - editorPosition.x,
      y: e.clientY - editorPosition.y
    });
    e.preventDefault(); // Prevent text selection during drag
  };
  
  const onDrag = (e) => {
    if (isDragging) {
      const clientX = e.clientX || (e.touches && e.touches[0].clientX);
      const clientY = e.clientY || (e.touches && e.touches[0].clientY);
      
      if (clientX && clientY) {
        const newX = clientX - dragStartPos.x;
        const newY = clientY - dragStartPos.y;
        
        // Keep editor within viewport bounds
        const width = modalRef.current?.offsetWidth || 400;
        const height = modalRef.current?.offsetHeight || 250;
        
        setEditorPosition({
          x: Math.max(0, Math.min(newX, window.innerWidth - width)),
          y: Math.max(0, Math.min(newY, window.innerHeight - height))
        });
      }
    }
  };
  
  const stopDrag = () => {
    setIsDragging(false);
  };
  
  // TOUCH DRAG HANDLERS
  const startTouchDrag = (e) => {
    if (e.touches && e.touches[0]) {
      setIsDragging(true);
      setDragStartPos({
        x: e.touches[0].clientX - editorPosition.x,
        y: e.touches[0].clientY - editorPosition.y
      });
      // Don't prevent default here to allow scrolling on mobile
    }
  };
  
  const onTouchDrag = (e) => {
    if (isDragging && e.touches && e.touches[0]) {
      const newX = e.touches[0].clientX - dragStartPos.x;
      const newY = e.touches[0].clientY - dragStartPos.y;
      
      // Keep editor within viewport bounds
      const width = modalRef.current?.offsetWidth || 400;
      const height = modalRef.current?.offsetHeight || 250;
      
      setEditorPosition({
        x: Math.max(0, Math.min(newX, window.innerWidth - width)),
        y: Math.max(0, Math.min(newY, window.innerHeight - height))
      });
      
      // Prevent scrolling when dragging
      e.preventDefault();
    }
  };
  
  const stopTouchDrag = () => {
    setIsDragging(false);
  };
  
  // Setup drag event listeners
  useEffect(() => {
    if (isDragging) {
      // Mouse events
      document.addEventListener('mousemove', onDrag);
      document.addEventListener('mouseup', stopDrag);
      
      // Touch events
      document.addEventListener('touchmove', onTouchDrag, { passive: false });
      document.addEventListener('touchend', stopTouchDrag);
      document.addEventListener('touchcancel', stopTouchDrag);
    }
    
    return () => {
      // Mouse events
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', stopDrag);
      
      // Touch events
      document.removeEventListener('touchmove', onTouchDrag);
      document.removeEventListener('touchend', stopTouchDrag);
      document.removeEventListener('touchcancel', stopTouchDrag);
    };
  }, [isDragging]);
  
  return (
    <div className="inline-math-field relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#32289a]"
        placeholder={placeholder}
        rows={rows}
      />
      
      <div className="flex justify-end mt-1">
        <button
          onClick={openMathEditor}
          className="text-xs text-gray-400 flex items-center"
          title="Insert mathematical expression (Ctrl+M)"
        >
          <span className="mr-1 text-xs font-serif italic">Σ</span>
          Add math (Ctrl+M)
        </button>
      </div>
      
      {/* Draggable Math Editor Modal */}
      {showMathEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 overflow-hidden">
          <div 
            ref={modalRef}
            className="absolute bg-white rounded-lg shadow-lg"
            style={{ 
              left: `${editorPosition.x}px`, 
              top: `${editorPosition.y}px`,
              width: '400px',
              maxWidth: '95vw'
            }}
          >
            <div className="flex items-center justify-between bg-gray-100 px-4 py-2 rounded-t-lg">
              <div
                onMouseDown={startDrag}
                onTouchStart={startTouchDrag}
                className="cursor-move bg-gray-200 hover:bg-gray-300 p-2 rounded mr-2 flex items-center justify-center"
                title="Drag to move"
              >
                <GripVertical size={16} className="mr-1" />
                <span className="text-xs">Drag</span>
              </div>
              <h3 className="ml-2 text-sm md:text-lg font-medium flex-grow">Math Editor</h3>
              <button 
                onClick={() => closeMathEditor(false)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none p-2"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-2">
                <math-field
                  ref={mathEditorRef}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#32289a] min-h-[60px]"
                  style={{ 
                    backgroundColor: '#fff',
                    fontSize: '1.1em'
                  }}
                  value={mathContent}
                  onChange={(e) => setMathContent(e.target.value)}
                  virtual-keyboard-mode="onfocus"
                  virtual-keyboards="all"
                  smart-fence="true"
                  smart-superscript="true"
                ></math-field>
                
                {/* Added instruction for creating a new line */}
                <div className="text-xs text-gray-500 mt-1">
                  Press Ctrl+Enter to add a new line
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <button 
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  onClick={() => closeMathEditor(false)}
                >
                  Cancel
                </button>
                <button 
                  className="px-4 py-2 bg-[#32289a] text-white rounded-md hover:bg-opacity-90"
                  onClick={() => closeMathEditor(true)}
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineMathField;
